import { useState, useEffect, useCallback } from 'react';
import { getAdminPayments } from '../../api/adminApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

const statusStyle = {
  pending:    { color: '#ffaa44', bg: 'rgba(255,170,68,0.12)', border: 'rgba(255,170,68,0.3)' },
  confirmed:  { color: '#66ff99', bg: 'rgba(102,255,153,0.12)', border: 'rgba(102,255,153,0.3)' },
  failed:     { color: '#ff6666', bg: 'rgba(200,50,50,0.12)', border: 'rgba(200,50,50,0.3)' },
  cancelled:  { color: '#888',    bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.1)' },
  expired:    { color: '#666',    bg: 'rgba(100,100,100,0.1)', border: 'rgba(100,100,100,0.2)' },
};

const AdminPayments = () => {
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  const load = useCallback(() => {
    setLoading(true);
    getAdminPayments({ page, limit: 20, status, search })
      .then((res) => { setPayments(res.data.payments); setTotal(res.data.total); setPages(res.data.pages); })
      .catch(() => setPayments([]))
      .finally(() => setLoading(false));
  }, [page, status, search]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Deposit Intent</h4>
      <div style={{ color: '#555', fontSize: 13, marginBottom: 20 }}>{total} {status !== 'all' ? status : 'total'} deposit{total !== 1 ? 's' : ''}</div>

      {/* Filters */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
        {/* Search */}
        <div>
          <input
            type="text"
            placeholder="Search by user code or email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{
              width: '100%',
              background: 'rgba(0,229,255,0.04)',
              border: '1px solid rgba(0,229,255,0.2)',
              borderRadius: 8,
              padding: '10px 14px',
              color: '#fff',
              fontSize: 13,
              outline: 'none',
            }}
          />
        </div>

        {/* Status Filter */}
        <div style={{ maxWidth: 300 }}>
          <label style={{ display: 'block', color: '#888', fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Status</label>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            style={{
              width: '100%',
              background: '#1a1a1a',
              border: '1px solid rgba(0,229,255,0.2)',
              borderRadius: 8,
              padding: '8px 12px',
              color: '#fff',
              fontSize: 13,
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="all" style={{ background: '#1a1a1a', color: '#fff' }}>All Status</option>
            <option value="pending" style={{ background: '#1a1a1a', color: '#ffaa44' }}>Pending</option>
            <option value="confirmed" style={{ background: '#1a1a1a', color: '#66ff99' }}>Confirmed</option>
            <option value="failed" style={{ background: '#1a1a1a', color: '#ff6666' }}>Failed</option>
            <option value="cancelled" style={{ background: '#1a1a1a', color: '#888' }}>Cancelled</option>
            <option value="expired" style={{ background: '#1a1a1a', color: '#666' }}>Expired</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, overflow: isMobile ? 'auto' : 'hidden', overflowY: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '800px' : '100%' }}>
          <thead>
            <tr style={{ background: 'rgba(0,229,255,0.07)' }}>
              {['User', 'Amount', 'Status', 'Payment Address', 'Received', 'TX Hash', 'Date'].map((h) => (
                <th key={h} style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>Loading...</td></tr>
            ) : payments.length === 0 ? (
              <tr><td colSpan={7} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>No deposits found.</td></tr>
            ) : payments.map((p) => {
              const ss = statusStyle[p.status] || statusStyle.pending;
              return (
                <tr key={p._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ color: '#fff', fontSize: 13 }}>{p.user?.name}</div>
                    <div style={{ color: '#555', fontSize: 11 }}>{p.user?.userCode || p.user?.email}</div>
                  </td>
                  <td style={{ color: '#fff', fontWeight: 700, fontSize: 13, padding: '11px 14px' }}>${p.amount.toFixed(2)}</td>
                  <td style={{ padding: '11px 14px' }}>
                    <span style={{ background: ss.bg, border: `1px solid ${ss.border}`, color: ss.color, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>
                      {p.status}
                    </span>
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ color: '#00e5ff', fontSize: 11, fontFamily: 'monospace', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', title: p.paymentAddress }}>
                      {p.paymentAddress || '—'}
                    </div>
                  </td>
                  <td style={{ color: '#66ff99', fontSize: 12, padding: '11px 14px', fontWeight: 600 }}>
                    {p.receiveAmount ? `${p.receiveAmount} ${p.receiveCurrency}` : '—'}
                  </td>
                  <td style={{ color: '#444', fontSize: 10, fontFamily: 'monospace', padding: '11px 14px', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', title: p.transactionHash }}>
                    {p.transactionHash ? p.transactionHash.slice(0, 8) + '...' : '—'}
                  </td>
                  <td style={{ color: '#555', fontSize: 11, padding: '11px 14px', whiteSpace: 'nowrap' }}>{fmtIST(p.createdAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          {Array.from({ length: Math.min(pages, 10) }, (_, i) => i + 1).map((p) => (
            <button key={p} onClick={() => setPage(p)}
              style={{ background: p === page ? cyan : 'rgba(255,255,255,0.06)', border: 'none', color: p === page ? '#000' : '#888', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPayments;
